'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getCpImageList, getCpVideoList, getCpPornAlbumList } from '@/api/couple/cpInfo';
import { Slick } from '@/components/slick/slick';
import { useCpMediaStore } from '@/store/cpMedia';
import { CpImageType, CpVideoType, CpPornAlbumList, DetailInfo } from '@/types/cpType';

import ImagePage from './(image)/i/imagePage';
import AlbumPage from './(porn)/p/albumPage';
import VideoPage from './(video)/v/videoPage';
import CpInfo from './cpInfo';

import style from './tabContent.module.scss';

function TabContent({
    userId,
    initStatus,
    initImageData,
    initVideoData,
    initAlbumData
}: {
    userId: string;
    initStatus: string;
    initImageData?: CpImageType[];
    initVideoData?: CpVideoType[];
    initAlbumData?: CpPornAlbumList[];
}) {
    const [secondRender, setSecondRender] = useState(false);
    const [imageList, setImageList] = useState<CpImageType[]>(initImageData || []);
    const [videoList, setVideoList] = useState<CpVideoType[]>(initVideoData || []);
    const [albumList, setAlbumList] = useState<CpPornAlbumList[]>(initAlbumData || []);

    const switchCpMedia = useCpMediaStore.use.switchCpMedia();
    const setTargetMedia = useCpMediaStore.use.setTargetMedia();
    const close = useCpMediaStore.use.close();
    const setKeepUrl = useCpMediaStore.use.setKeepUrl();

    const route = usePathname().split('/');
    const status = route[route.length - 1] === userId.toString() ? null : route[route.length - 1];
    const queryText = typeof location !== 'undefined' ? location?.search : '';
    const tabList = [
        {
            label: '介紹',
            href: `/cp/${userId}${queryText}`,
            status: null
        },
        {
            label: '照片',
            href: `/cp/${userId}/i${queryText}`,
            status: 'i'
        },
        {
            label: '影片',
            href: `/cp/${userId}/v${queryText}`,
            status: 'v'
        },
        {
            label: '迷片',
            href: `/cp/${userId}/p${queryText}`,
            status: 'p'
        }
    ];

    useEffect(() => {
        setSecondRender(true);
    }, []);

    const initialSlide = status ? tabList.findIndex(tab => tab.status === status) : 0;

    useEffect(() => {
        const initFetchImageList = async () => {
            if (initStatus === 'image') return;
            const res = await getCpImageList({ userId, page: 1, limit: 12 });
            if (res.status === 200) {
                setImageList(res.data);
            }
        };

        const initFetchVideoList = async () => {
            if (initStatus === 'video') return;
            const res = await getCpVideoList({ userId, page: 1, limit: 12 });
            if (res.status === 200) {
                setVideoList(res.data);
            }
        };

        const initFetchPornAlbumList = async () => {
            if (initStatus === 'porn') return;
            const res = await getCpPornAlbumList({ userId, page: 1, limit: 6 });
            if (res.status === 200) {
                setAlbumList(res.data);
            }
        };

        initFetchImageList();
        initFetchVideoList();
        initFetchPornAlbumList();
    }, [userId, initStatus]);

    const onSlickEnd = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const currentPath = usePathname();

    const switchDetail = (params?: DetailInfo) => {
        if (params) {
            setKeepUrl(currentPath);
            setTargetMedia(params);
            switchCpMedia(true);
        } else {
            close();
        }
    };

    return (
        <>
            <div className={style.tabContent}>
                <Slick
                    autoHeight
                    className={style.slick}
                    initialSlide={initialSlide}
                    onSlickEnd={onSlickEnd}
                    resetHeightKey="cpInfo"
                    styling="underline"
                    tabs={tabList}
                >
                    <div className={style.largeGap}>
                        {secondRender || status === null ? <CpInfo /> : null}
                    </div>
                    <div className={style.largeGap}>
                        {secondRender || status === 'image' ? (
                            <ImagePage
                                userId={userId}
                                imageList={imageList}
                                setImageList={setImageList}
                                switchDetail={switchDetail}
                            />
                        ) : null}
                    </div>
                    <div className={style.largeGap}>
                        {secondRender || status === 'video' ? (
                            <VideoPage
                                userId={userId}
                                videoList={videoList}
                                setVideoList={setVideoList}
                                switchDetail={switchDetail}
                            />
                        ) : null}
                    </div>
                    <div className={style.largeGap}>
                        {secondRender || status === 'porn' ? (
                            <AlbumPage
                                userId={userId}
                                albumList={albumList}
                                setAlbumList={setAlbumList}
                                switchDetail={switchDetail}
                            />
                        ) : null}
                    </div>
                </Slick>
                <div className={style.homeIndicatorPlaceHolder} />
            </div>
        </>
    );
}

export default TabContent;
